/*
 * Cart drawer — AJAX cart experience.
 *
 * - Intercepts product "Add to cart" form submissions and posts to /cart/add.js.
 * - Opens a right-side drawer instead of redirecting to /cart.
 * - Handles quantity changes and item removal via /cart/change.js.
 * - "Ordine Prioritario" toggle: adds/removes a real variant as a line item
 *   (always quantity 1, excluded from the product rows and discount logic).
 *   It reuses the same hidden cart-addon product/variant this toggle always
 *   used (still literally handled "shipping-protection" in Shopify admin —
 *   theme code can't rename or create Shopify products) since insurance
 *   itself is no longer a paid add-on; only the presentation, default state
 *   (on) and session-persisted opt-out changed.
 * - Discounts: only real Shopify discount data is shown. Per line item, a real
 *   Shopify discount (e.g. the BOGO "PRENDI 2, PAGHI 1") strikes through
 *   original_line_price next to final_line_price and lists its title + amount
 *   from line_level_discount_allocations. The footer "Sconti" block aggregates
 *   cart- and line-level discount applications. Compare-at prices are never
 *   used — nothing is faked. The total is always the real cart.total_price.
 * - Keeps the header cart count in sync.
 * - Falls back to native Shopify cart behaviour if any request fails.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-cart-drawer]');
  if (!root) return;

  var panel = root.querySelector('[data-cart-panel]');
  var itemsEl = root.querySelector('[data-cart-items]');
  var subtotalEl = root.querySelector('[data-cart-subtotal]');
  var titleCountEl = root.querySelector('[data-cart-title-count]');
  var countEls = document.querySelectorAll('[data-cart-count]');

  var priorityRow = root.querySelector('[data-cart-priority]');
  var priorityToggle = root.querySelector('[data-priority-toggle]');
  var savingsEl = root.querySelector('[data-cart-savings]');
  var savingsAmountEl = root.querySelector('[data-cart-savings-amount]');
  var savingsPillEl = root.querySelector('[data-cart-savings-pill]');


  var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
  var addUrl = (root.getAttribute('data-url-add') || '/cart/add') + '.js';
  var changeUrl = (root.getAttribute('data-url-change') || '/cart/change') + '.js';
  var cartUrl = (root.getAttribute('data-url-cart') || '/cart') + '.js';

  var priorityId = root.getAttribute('data-priority-id') || '';
  var priorityHandle = root.getAttribute('data-priority-handle') || 'shipping-protection';

  // Presentational "PRENDI N, PAGHI N/2" template. The actual free items and
  // amounts always come from Shopify; only this wording is derived from the
  // eligible-watch quantity.
  var promoTemplate = root.getAttribute('data-promo-label') || 'PRENDI {take}, PAGHI {pay}';
  var currentPromoLabel = null;

  var labels = {
    remove: root.getAttribute('data-label-remove') || 'Rimuovi',
    increase: root.getAttribute('data-label-increase') || 'Aumenta quantità',
    decrease: root.getAttribute('data-label-decrease') || 'Diminuisci quantità',
    save: root.getAttribute('data-label-save') || 'Risparmi'
  };

  var currentCart = null;
  var lastFocused = null;
  var isBusy = false;

  /* ---------- Money formatting (Shopify standard) ---------- */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = moneyFormat.match(placeholder);
    if (!match) return moneyFormat;

    function number(value, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(value) || value == null) return '0';
      value = (value / 100.0).toFixed(precision);
      var parts = value.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }

    var value;
    switch (match[1]) {
      case 'amount': value = number(cents, 2); break;
      case 'amount_no_decimals': value = number(cents, 0); break;
      case 'amount_with_comma_separator': value = number(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = number(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = number(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = number(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = number(cents, 0, ' ', ','); break;
      default: value = number(cents, 2);
    }
    return moneyFormat.replace(placeholder, value);
  }

  /* ---------- Helpers ---------- */
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Format a trusted product title: escape it, then raise ® as a small
  // superscript (mirrors snippets/product-title.liquid). Safe because the
  // title is HTML-escaped first, so only our own <sup> markup is injected.
  function formatProductTitle(str) {
    return escapeHtml(str).replace(/®/g, '<sup class="product-title__registered">®</sup>');
  }

  function sizedImage(url, width) {
    if (!url) return '';
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', width);
      return u.href;
    } catch (e) {
      return url;
    }
  }

  function setBusy(state) {
    isBusy = state;
    root.classList.toggle('is-busy', state);
  }

  function toggleHidden(el, hidden) {
    if (el) el.classList.toggle('is-hidden', hidden);
  }

  // Matches by the SAME underlying handle/variant this add-on always used —
  // an existing cart line added before this turn's rename is recognised
  // exactly the same way, no migration step needed.
  function isPriorityItem(item) {
    if (priorityHandle && item.handle === priorityHandle) return true;
    if (priorityId && String(item.variant_id) === String(priorityId)) return true;
    return false;
  }

  // Eligible promotional watches: the same set the Mix & Match cart state uses
  // (everything except the Ordine Prioritario add-on), also excluding gift
  // cards. Accessories in this catalogue are only that add-on.
  function isEligibleWatch(item) {
    return !isPriorityItem(item) && !item.gift_card;
  }

  // Dynamic promo wording from the eligible quantity:
  //   paid = floor(qty / 2), take = paid * 2  → "PRENDI {take}, PAGHI {pay}".
  // Fewer than two eligible watches → no activated-discount label.
  function buildPromoLabel(count) {
    var paid = Math.floor(count / 2);
    if (paid < 1) return null;
    return promoTemplate.replace('{take}', paid * 2).replace('{pay}', paid);
  }

  // The label overrides the Shopify discount title only for the promotion on
  // eligible watches; any other discount keeps its real title.
  function promoTitleFor(item, realTitle) {
    if (currentPromoLabel && isEligibleWatch(item)) return currentPromoLabel;
    return realTitle;
  }

  function findPriorityItem(cart) {
    if (!cart) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (isPriorityItem(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  // Ordine Prioritario starts ON by default (as if the customer had already
  // tapped it) but a manual opt-out must stick for the rest of the session
  // instead of being silently re-enabled on the next cart open or re-render.
  // Same sessionStorage-flag pattern the reservation timer used — no new
  // state system.
  var PRIORITY_OPT_OUT_KEY = 'velarePriorityOrderOff';
  var priorityAutoInitDone = false;

  function priorityOptedOut() {
    try { return sessionStorage.getItem(PRIORITY_OPT_OUT_KEY) === '1'; } catch (e) { return false; }
  }

  function setPriorityOptedOut(value) {
    try {
      if (value) sessionStorage.setItem(PRIORITY_OPT_OUT_KEY, '1');
      else sessionStorage.removeItem(PRIORITY_OPT_OUT_KEY);
    } catch (e) { /* ignore */ }
  }

  function fetchCart() {
    return fetch(cartUrl, { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /* ---------- Rendering ---------- */
  function itemHTML(item) {
    // Prefer the variant image, then the line-item featured image.
    var image = item.image || (item.featured_image && item.featured_image.url) || '';
    var thumb = image
      ? '<img src="' + escapeHtml(sizedImage(image, 360)) + '" alt="' + escapeHtml(item.product_title) +
        '" width="120" height="120" loading="lazy">'
      : '';

    var variant = '';
    if (item.variant_title && item.options_with_values && item.options_with_values.length) {
      variant = item.options_with_values
        .map(function (opt) {
          return escapeHtml(opt.name) + ': ' + escapeHtml(opt.value);
        })
        .join(' · ');
    }

    // Visible line-item properties (e.g. bundle "Orologio 1/2/3" color choices).
    // Properties whose key starts with "_" are hidden by Shopify convention.
    var props = '';
    if (item.properties) {
      var propLines = [];
      Object.keys(item.properties).forEach(function (k) {
        if (!k || k.charAt(0) === '_') return;
        var pv = item.properties[k];
        if (pv == null || pv === '') return;
        propLines.push(escapeHtml(k) + ': ' + escapeHtml(pv));
      });
      if (propLines.length) {
        props = '<p class="cart-drawer__variant cart-drawer__props">' + propLines.join('<br>') + '</p>';
      }
    }

    // Real Shopify discount on this line (e.g. the BOGO offer). Never compare-at:
    // only strike through the original when Shopify actually discounted the line.
    var originalHTML = '';
    if (item.original_line_price > item.final_line_price) {
      originalHTML = '<s class="cart-drawer__original">' + formatMoney(item.original_line_price) + '</s>';
    }

    // Free line (LF style): under "struck price  €0,00" add its own green
    // "(Risparmi €X)" line. Only when Shopify made the WHOLE line free — a
    // partially discounted line keeps just the strike + tag as before.
    var saveHTML = '';
    if (item.final_line_price === 0 && item.original_line_price > 0) {
      saveHTML = '<span class="cart-drawer__save-line">(' + labels.save + ' ' + formatMoney(item.original_line_price) + ')</span>';
    }

    // The applied offer, as the shared grey pill (same class, same tag icon
    // as the summary "Sconto" row — the icon lives in CSS, so there is no
    // second copy of it here). Name only: the amount is already on the
    // "(Risparmi €X)" line right above it.
    var discountHTML = (item.line_level_discount_allocations || [])
      .map(function (a) {
        var realTitle = (a.discount_application && a.discount_application.title) || 'Sconto';
        var title = promoTitleFor(item, realTitle);
        return (
          '<span class="cart-drawer__line-discount">' +
            '<span class="cart-drawer__disc-pill">' + escapeHtml(title) + '</span>' +
          '</span>'
        );
      })
      .join('');

    var trash =
      '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
      '<path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6m4 5v6m6-6v6" ' +
      'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    return (
      '<div class="cart-drawer__item" data-cart-item data-key="' + escapeHtml(item.key) + '" role="listitem">' +
        '<a class="cart-drawer__thumb" href="' + escapeHtml(item.url) + '">' + thumb + '</a>' +
        '<div class="cart-drawer__info">' +
          '<a class="cart-drawer__name product-title" href="' + escapeHtml(item.url) + '">' + formatProductTitle(item.product_title) + '</a>' +
          (variant ? '<p class="cart-drawer__variant">' + variant + '</p>' : '') +
          props +
          '<div class="cart-drawer__line">' +
            '<div class="cart-drawer__qty">' +
              '<button type="button" class="cart-drawer__qty-btn" data-qty-down aria-label="' + escapeHtml(labels.decrease) + '">&minus;</button>' +
              '<span class="cart-drawer__qty-val" data-qty-val>' + item.quantity + '</span>' +
              '<button type="button" class="cart-drawer__qty-btn" data-qty-up aria-label="' + escapeHtml(labels.increase) + '">+</button>' +
            '</div>' +
            '<span class="cart-drawer__prices">' +
              '<span class="cart-drawer__price-row">' +
                originalHTML +
                '<span class="cart-drawer__price">' + formatMoney(item.final_line_price) + '</span>' +
              '</span>' +
              saveHTML +
              discountHTML +
            '</span>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="cart-drawer__remove" data-remove aria-label="' + escapeHtml(labels.remove) + '">' + trash + '</button>' +
      '</div>'
    );
  }

  // One aggregate "RISPARMI" line instead of a per-title breakdown — the
  // real Shopify discount amounts (cart-level + every line's own) summed
  // into a single total. The per-line "PRENDI 2, PAGHI 1" tag on each item
  // (itemHTML, above) still shows its own real title + amount; only this
  // footer summary is a plain total now.
  function renderSavings(cart) {
    var total = 0;

    (cart.cart_level_discount_applications || []).forEach(function (d) {
      if (d.total_allocated_amount > 0) total += d.total_allocated_amount;
    });

    cart.items.forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (a) {
        if (a.amount > 0) total += a.amount;
      });
    });

    // The offer is explicit now (LF style): "Sconto  [PRENDI 2, PAGHI 1]
    // −€59,99" — one row. The pill carries the same dynamic promo label the
    // per-line tags use; if some other real discount is applied instead, its
    // own Shopify title shows. Amounts stay the real summed allocations.
    var pillTitle = currentPromoLabel;
    if (!pillTitle) {
      var apps = (cart.cart_level_discount_applications || []);
      for (var pi = 0; pi < cart.items.length && !pillTitle; pi++) {
        var allocs = cart.items[pi].line_level_discount_allocations || [];
        if (allocs.length && allocs[0].discount_application) pillTitle = allocs[0].discount_application.title;
      }
      if (!pillTitle && apps.length) pillTitle = apps[0].title;
    }

    if (total > 0) {
      if (savingsPillEl) {
        savingsPillEl.textContent = pillTitle || '';
        toggleHidden(savingsPillEl, !pillTitle);
      }

      if (savingsAmountEl) savingsAmountEl.innerHTML = '&minus;' + formatMoney(total);
      toggleHidden(savingsEl, false);
    } else {
      if (savingsAmountEl) savingsAmountEl.innerHTML = '';
      toggleHidden(savingsEl, true);
    }
  }

  function render(cart) {
    currentCart = cart;

    // A truly empty cart re-arms the default-on init, so a watch added later
    // brings the fee back (removeLine's empty-cart cleanup drops the lone fee
    // without recording an opt-out).
    if (cart.item_count === 0) priorityAutoInitDone = false;

    var watchItems = [];
    var watchCount = 0;
    var promoCount = 0;
    var priorityItem = null;

    cart.items.forEach(function (item) {
      if (isPriorityItem(item)) {
        priorityItem = item;
        return;
      }
      watchItems.push(item);
      watchCount += item.quantity;
      if (isEligibleWatch(item)) promoCount += item.quantity;
    });

    // Dynamic promo wording used by itemHTML + renderSavings below.
    currentPromoLabel = buildPromoLabel(promoCount);

    // Header + title counts reflect products only (Ordine Prioritario is an add-on).
    countEls.forEach(function (el) {
      el.textContent = watchCount;
      el.classList.toggle('is-hidden', watchCount === 0);
    });
    if (titleCountEl) {
      titleCountEl.textContent = watchCount;
      titleCountEl.classList.toggle('is-hidden', watchCount === 0);
    }

    // Empty state only when the cart truly has nothing in it.
    root.classList.toggle('cart-drawer--empty', cart.item_count === 0);

    // DISPLAY-ONLY sort: the free line(s) (final price 0 with a real
    // original) render first, LF style. Sorting a copy of the array changes
    // nothing about the cart or its keys — every control on a row acts via
    // its own data-key, so the steppers, the trash icon and the swap module
    // keep hitting the exact line they belong to whatever the visual order.
    var displayItems = watchItems.slice().sort(function (a, b) {
      var aFree = a.final_line_price === 0 && a.original_line_price > 0 ? 0 : 1;
      var bFree = b.final_line_price === 0 && b.original_line_price > 0 ? 0 : 1;
      return aFree - bFree;
    });

    itemsEl.innerHTML = displayItems
      .map(function (item) {
        return itemHTML(item);
      })
      .join('');

    // Total always stays the real Shopify cart total.
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    // Publish the cart state AND sync the Mix & Match module as part of THIS
    // rebuild — right after the items are written, on EVERY render path
    // (including the two early returns below). The module's POSITION is fixed by
    // the cart-drawer template (rendered inline, always directly below the line
    // items), so nothing here moves it; syncCartCarousel only refreshes its
    // visibility and content from the state publishState just set. Running it on
    // every render keeps that in step even on the early-return paths, which the
    // separate velare:cart event skips.
    publishState(cart, watchCount);
    if (window.Velare && window.Velare.syncCartCarousel) window.Velare.syncCartCarousel();

    // Default-on: the moment the cart holds anything and the add-on isn't
    // already there, add it automatically — unless the customer already opted
    // out this session, in which case that choice sticks. Guarded by
    // priorityAutoInitDone so this fires at most once per page load (no
    // double-add across re-renders); isBusy already prevents addVariant from
    // racing itself.
    if (!priorityItem && !priorityAutoInitDone && !priorityOptedOut() && !isBusy && priorityId && cart.item_count > 0) {
      priorityAutoInitDone = true;
      addVariant(priorityId, 1);
      return;
    }

    // Ordine Prioritario toggle state.
    if (priorityToggle) {
      var on = !!priorityItem;
      priorityToggle.setAttribute('aria-checked', on ? 'true' : 'false');
      priorityToggle.classList.toggle('is-on', on);
      if (priorityRow) priorityRow.classList.toggle('is-on', on);
    }

    // Safety net: the add-on must never exceed quantity 1.
    if (priorityItem && priorityItem.quantity > 1 && !isBusy) {
      changeItem(priorityItem.key, 1);
      return;
    }

    renderSavings(cart);
  }

  // Publish the eligible-watch state (Ordine Prioritario add-on excluded, line-item
  // quantities counted) so the 2x1 UI on product pages and product cards can
  // tell "starting a pair" from "completing a pair" without its own polling.
  // Read-only broadcast: it changes nothing about the drawer itself.
  function publishState(cart, watchCount) {
    var detail = {
      cart: cart,
      watchCount: watchCount,
      pending: watchCount % 2 === 1
    };
    window.Velare = window.Velare || {};
    window.Velare.bundleState = detail;
    document.dispatchEvent(new CustomEvent('velare:cart', { detail: detail }));
  }

  // Fetch the live cart and render. Only real Shopify data is used.
  function refresh() {
    return fetchCart().then(function (cart) {
      render(cart);
      return cart;
    });
  }

  // Let the 2x1 UI re-sync the cart state without opening the drawer (a product
  // card adding the first watch keeps the customer on the catalogue page).
  window.Velare = window.Velare || {};
  window.Velare.refreshCart = refresh;

  /* ---------- Open / close ---------- */
  function getFocusable() {
    return Array.prototype.slice
      .call(panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter(function (el) {
        return el.offsetParent !== null;
      });
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'Tab') {
      var f = getFocusable();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ---------- Reserved-cart bar (display only) ----------
   * A 5-minute MM:SS countdown. The end timestamp is written to
   * sessionStorage the FIRST time the drawer opens and reused on every later
   * open, so closing and reopening continues from where it was instead of
   * restarting. At zero it clamps to 00:00 and the interval stops — nothing
   * is removed, nothing redirects. A fresh session starts a fresh window. */
  var RESERVE_KEY = 'velareCartReserveEnd';
  var RESERVE_MINUTES = 5;
  var reservedClock = root.querySelector('[data-cart-reserved-clock]');
  var reserveInterval = null;

  function reserveLeft() {
    var end = 0;
    try { end = parseInt(sessionStorage.getItem(RESERVE_KEY), 10) || 0; } catch (e) { /* ignore */ }
    return Math.max(0, end - Date.now());
  }

  function reserveTick() {
    if (!reservedClock) return;
    var left = reserveLeft();
    var m = Math.floor(left / 60000);
    var sec = Math.floor((left % 60000) / 1000);
    reservedClock.textContent = (m < 10 ? '0' : '') + m + ':' + (sec < 10 ? '0' : '') + sec;
    if (left <= 0 && reserveInterval) {
      clearInterval(reserveInterval);
      reserveInterval = null;
    }
  }

  function startReserve() {
    if (!reservedClock) return;
    var end = 0;
    try { end = parseInt(sessionStorage.getItem(RESERVE_KEY), 10) || 0; } catch (e) { /* ignore */ }
    if (!end) {
      end = Date.now() + RESERVE_MINUTES * 60000;
      try { sessionStorage.setItem(RESERVE_KEY, String(end)); } catch (e) { /* ignore */ }
    }
    reserveTick();
    if (!reserveInterval && reserveLeft() > 0) reserveInterval = setInterval(reserveTick, 1000);
  }

  function open() {
    if (root.classList.contains('is-open')) return;
    lastFocused = document.activeElement;
    startReserve();
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    document.addEventListener('keydown', onKeydown);
    var closeBtn = root.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  /* ---------- Cart mutations ---------- */
  function addFromForm(form, submitter) {
    if (isBusy) return;
    setBusy(true);
    var btn = submitter || form.querySelector('[name="add"]');
    if (btn) btn.classList.add('is-loading');

    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(refresh)
      .then(function () {
        open();
      })
      .catch(function () {
        // Graceful fallback: native submit adds the item and lands on /cart.
        form.submit();
      })
      .finally(function () {
        setBusy(false);
        if (btn) btn.classList.remove('is-loading');
      });
  }

  function addVariant(id, quantity) {
    if (isBusy) return;
    setBusy(true);
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: id, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(refresh)
      .catch(function () {
        refresh().catch(function () {});
      })
      .finally(function () {
        setBusy(false);
      });
  }

  // Raw /cart/change.js POST — shared by changeItem and removeLine. Resolves
  // with the updated cart JSON; rejects on a non-2xx response.
  function postChange(key, quantity) {
    return fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) {
      if (!r.ok) throw new Error('change failed');
      return r.json();
    });
  }

  function changeItem(key, quantity) {
    if (isBusy) return;
    setBusy(true);
    postChange(key, quantity)
      .then(refresh)
      .catch(function () {
        refresh().catch(function () {});
      })
      .finally(function () {
        setBusy(false);
      });
  }

  // The Ordine Prioritario fee can never be the only thing in the cart. Would
  // setting `key` to `quantity` leave nothing but that fee behind? True only
  // when the line is being removed outright (quantity 0), it's the last
  // remaining watch line, and the fee is currently present.
  function wouldStrandPriority(key, quantity) {
    if (quantity !== 0) return false;
    if (!currentCart || !currentCart.items) return false;
    var hasPriority = false;
    var otherWatchUnits = 0;
    currentCart.items.forEach(function (it) {
      if (isPriorityItem(it)) { hasPriority = true; return; }
      if (it.key !== key) otherWatchUnits += it.quantity;
    });
    return hasPriority && otherWatchUnits === 0;
  }

  // Remove or decrement a watch line. If this takes the cart's last watch and
  // only the Ordine Prioritario fee would remain, drop the fee in the SAME
  // flow so the cart empties to its normal empty state — the fee can't stand
  // alone. isBusy guards against double submissions.
  function removeLine(key, quantity) {
    if (isBusy) return;
    if (!wouldStrandPriority(key, quantity)) {
      changeItem(key, quantity);
      return;
    }
    var priorityItem = findPriorityItem(currentCart);
    setBusy(true);
    postChange(key, quantity)
      .then(function () { return priorityItem ? postChange(priorityItem.key, 0) : null; })
      .then(refresh)
      .catch(function () {
        refresh().catch(function () {});
      })
      .finally(function () {
        setBusy(false);
      });
  }

  /* ---------- Events ---------- */
  // Intercept "Add to cart" submissions anywhere on the page.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    // Let dynamic checkout / payment buttons keep their native behaviour.
    var submitter = e.submitter;
    if (submitter && submitter.name && submitter.name !== 'add') return;

    e.preventDefault();
    addFromForm(form, submitter);
  });

  // Header cart icon opens the drawer instead of navigating to /cart.
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-cart-toggle]');
    if (!toggle) return;
    e.preventDefault();
    open();
    refresh().catch(function () {});
  });

  // In-drawer interactions (close, quantity, remove).
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      close();
      return;
    }

    var item = e.target.closest('[data-cart-item]');
    if (!item) return;

    var key = item.getAttribute('data-key');
    var valEl = item.querySelector('[data-qty-val]');
    var current = parseInt(valEl ? valEl.textContent : '1', 10) || 1;

    // The trash icon is the single removal control now (the "Rimuovi" text link
    // and the 4x2 "Cambia gratis" swap link were both removed). removeLine also
    // clears the lone Ordine Prioritario fee when the last watch leaves.
    if (e.target.closest('[data-remove]')) {
      removeLine(key, 0);
    } else if (e.target.closest('[data-qty-up]')) {
      changeItem(key, current + 1);
    } else if (e.target.closest('[data-qty-down]')) {
      removeLine(key, Math.max(0, current - 1));
    }
  });

  // Ordine Prioritario toggle (static row, bound once).
  if (priorityToggle) {
    priorityToggle.addEventListener('click', function () {
      if (isBusy) return;
      var on = priorityToggle.getAttribute('aria-checked') === 'true';
      var existing = findPriorityItem(currentCart);
      // Record the manual choice immediately (not inside the async .then) so
      // it's captured even if the request is still in flight when the drawer
      // closes — turning off persists for the session; turning back on clears
      // it so a later render won't skip the default-on init.
      setPriorityOptedOut(on);
      if (on) {
        if (existing) changeItem(existing.key, 0);
      } else if (existing) {
        changeItem(existing.key, 1);
      } else if (priorityId) {
        addVariant(priorityId, 1);
      }
    });
  }

  /* ---------- Init ---------- */
  refresh().catch(function () {});
})();
